import { Button } from "@admin/components/atoms/Button";
import { Modal, ModalProps } from "@admin/components/atoms/Modal";
import { updateLocationStatus } from "@enos-monorepo/lib";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { Trans, useTranslation } from "react-i18next";
import { z } from "zod";

interface ActivateLocationModalProps extends ModalProps {
  locationId: string;
  locationName: string;
}
export function ActivateLocationModal({
  locationName,
  locationId,

  ...props
}: ActivateLocationModalProps) {
  const { t } = useTranslation();
  const updateLocationSchema = z.object({
    locationId: z.string().min(1),
    status: z.boolean(),
  });

  type UpdateLocationData = z.infer<typeof updateLocationSchema>;

  const queryClient = useQueryClient();
  const { handleSubmit, setValue, reset } = useForm<UpdateLocationData>({
    resolver: zodResolver(updateLocationSchema),
    defaultValues: {
      locationId: locationId,
      status: false,
    },
  });
  useEffect(() => {
    setValue("locationId", locationId);
  }, [locationId, setValue]);
  const mutation = useMutation({
    mutationFn: updateLocationStatus,
    onSuccess: () => {
      reset();
      queryClient.invalidateQueries({ queryKey: ["allLocations"] });

      props?.onClose?.();
      toast.success(t("location.The location has been activated"), {
        duration: 4000,
      });
    },
    onError: (err: Error) => {
      toast.error(t("location.Unable to activate location"), {
        duration: 4000,
      });
    },
  });

  const onSubmit = (data: UpdateLocationData) => {
    mutation.mutate(data);
  };

  return (
    <Modal
      title={t("location.Activate", { locationName })}
      {...props}
      actionButtons={
        <>
          <Button
            variant="secondary"
            type="button"
            onClick={() => {
              props?.onClose?.();
              reset();
            }}
          >
            {t("common.Discard")}
          </Button>
          <Button
            variant="primary"
            property="success"
            type="submit"
            loading={mutation.isPending}
            onClick={handleSubmit(onSubmit)}
          >
            {t("location.Activate Location")}
          </Button>
        </>
      }
    >
      <form className="flex flex-col gap-5">
        <div>
          <Trans
            i18nKey="location.Are you sure you want to activate location"
            values={{ locationName }}
            components={{ strong: <strong /> }}
          />
        </div>
      </form>
    </Modal>
  );
}
